import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './GlobalSearch.css';

export default function GlobalSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const performSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);

    try {
      const allResults = [];

      // Search clients
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, name, email, phone')
        .or(`name.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%`)
        .limit(5);

      if (!clientsError && clients) {
        clients.forEach(client => {
          allResults.push({
            type: 'client',
            id: client.id,
            title: client.name,
            subtitle: client.email || client.phone || '',
            data: client,
          });
        });
      }

      // Search quotes
      const { data: quotes, error: quotesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total,
          date,
          clients (id, name)
        `)
        .eq('type', 'quote')
        .or(`invoice_number.ilike.%${term}%`)
        .limit(5);

      if (!quotesError && quotes) {
        quotes.forEach(quote => {
          allResults.push({
            type: 'quote',
            id: quote.id,
            title: `Quote ${quote.invoice_number || quote.id}`,
            subtitle: quote.clients?.name || 'Unknown Client',
            data: quote,
          });
        });
      }

      // Search invoices
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total,
          date,
          status,
          clients (id, name)
        `)
        .eq('type', 'invoice')
        .or(`invoice_number.ilike.%${term}%`)
        .limit(5);

      if (!invoicesError && invoices) {
        invoices.forEach(invoice => {
          allResults.push({
            type: 'invoice',
            id: invoice.id,
            title: `Invoice ${invoice.invoice_number || invoice.id}`,
            subtitle: `${invoice.clients?.name || 'Unknown Client'} - $${parseFloat(invoice.total || 0).toFixed(2)}`,
            data: invoice,
          });
        });
      }

      setResults(allResults);
      setShowResults(allResults.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  const handleResultClick = (result) => {
    if (result.type === 'client') {
      navigate(`/clients/${result.id}`);
    } else if (result.type === 'quote') {
      navigate(`/clients/${result.data.clients?.id}`, { state: { activeTab: 'history', invoiceId: result.data.id } });
    } else if (result.type === 'invoice') {
      navigate(`/clients/${result.data.clients?.id}`, { state: { activeTab: 'history', invoiceId: result.data.id } });
    }
    setSearchTerm('');
    setShowResults(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding results to allow clicking on them
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  return (
    <div className="global-search">
      <div className="search-input-wrapper">
        <input
          type="text"
          className="search-input"
          placeholder="Search clients, quotes, invoices..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
        <span className="search-icon">🔍</span>
        {loading && <span className="search-loading">...</span>}
      </div>

      {showResults && results.length > 0 && (
        <div className="search-results">
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}-${index}`}
              className="search-result-item"
              onClick={() => handleResultClick(result)}
            >
              <div className="result-icon">
                {result.type === 'client' && '👤'}
                {result.type === 'quote' && '📋'}
                {result.type === 'invoice' && '🧾'}
              </div>
              <div className="result-content">
                <div className="result-title">{result.title}</div>
                <div className="result-subtitle">{result.subtitle}</div>
              </div>
              <div className="result-type-badge">{result.type}</div>
            </div>
          ))}
        </div>
      )}

      {showResults && searchTerm.length >= 2 && results.length === 0 && !loading && (
        <div className="search-results">
          <div className="search-no-results">No results found</div>
        </div>
      )}
    </div>
  );
}
